import React, { useState } from 'react';
import { Box, Button, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908C16.658 14.121 17.64 11.813 17.64 9.2z" fill="#4285F4" />
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
  </svg>
);

const Login: React.FC = () => {
  const { login, verify2FA } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires2FA && result.tempToken) {
        setTempToken(result.tempToken);
        setTwoFARequired(true);
      } else {
        navigate('/app');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await verify2FA(tempToken, twoFACode);
      navigate('/app');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  const cardSx = {
    p: 4, borderRadius: 2,
    boxShadow: '0 20px 50px rgba(7,24,45,0.08)',
    width: '100%', maxWidth: 480,
    borderColor: 'rgba(7,24,45,0.08)',
  };

  // ── 2FA screen ──────────────────────────────────────────────────────────────
  if (twoFARequired) {
    return (
      <Box sx={{ minHeight: 'calc(100vh - 260px)', display: 'grid', placeItems: 'center', px: 2, py: { xs: 5, md: 7 }, bgcolor: '#f5f7fb' }}>
        <Paper variant="outlined" sx={cardSx}>
          <Stack spacing={2} component="form" onSubmit={handle2FA}>
            <Typography variant="h5" fontWeight={700}>Two-factor authentication</Typography>
            <Typography variant="body2" color="text.secondary">
              Enter the 6-digit code from your authenticator app.
            </Typography>
            <TextField
              label="6-digit code"
              value={twoFACode}
              onChange={(e) => setTwoFACode(e.target.value)}
              inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              required
              fullWidth
              autoFocus
            />
            {error && <Typography variant="body2" color="error">{error}</Typography>}
            <Button type="submit" variant="contained" color="secondary" size="large" disabled={loading || twoFACode.length < 6}>
              {loading ? 'Verifying…' : 'Verify'}
            </Button>
            <Button variant="text" size="small" onClick={() => { setTwoFARequired(false); setError(null); }}>
              Back to login
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // ── Main login ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: 'calc(100vh - 260px)', display: 'grid', placeItems: 'center', px: 2, py: { xs: 5, md: 7 }, bgcolor: '#f5f7fb' }}>
      <Paper variant="outlined" sx={cardSx}>
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          <Typography variant="h5" fontWeight={700}>Welcome back</Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to unlock your latest rides.
          </Typography>

          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<GoogleIcon />}
            onClick={() => { window.location.href = `${BACKEND_URL}/api/auth/google`; }}
            sx={{ textTransform: 'none', borderColor: 'rgba(0,0,0,0.23)', color: 'text.primary', justifyContent: 'center', gap: 1 }}
          >
            Continue with Google
          </Button>

          <Divider>or</Divider>

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
          />
          <Box sx={{ textAlign: 'right', mt: -1 }}>
            <Button component={RouterLink} to="/forgot-password" size="small" sx={{ textTransform: 'none' }}>
              Forgot password?
            </Button>
          </Box>

          {error && <Typography variant="body2" color="error">{error}</Typography>}

          <Button type="submit" variant="contained" color="secondary" size="large" disabled={loading}>
            {loading ? 'Signing in…' : 'Login'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            New to RideSaathi?{' '}
            <Button component={RouterLink} to="/signup" size="small">Create an account</Button>
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Login;
